import React, { useState, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import useCreateNotes from "zustand-store/create-notes";

const SearchablePatientDropdown = ({ control, errors, getAllPatients, patientName, setValue, fullPatientDetails }) => {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);

  const { setCurrentPatient } = useCreateNotes();

  const dropdownRef = useRef(null);

  // Fetch patients - backend handles JWT → Cognito ID → Internal ID conversion
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);

        // Simple API call - backend handles all the ID conversion
        const data = await getAllPatients();

        // Get unique patient first names only
        const uniquePatients = data.reduce((acc, patient) => {
          const existingPatient = acc.find(p => p.firstName === patient.firstName);

          if (!existingPatient && patient.firstName !== 'Untitled') {
            acc.push({
              id: patient.id,
              firstName: patient.firstName
            });
          }

          return acc;
        }, []);

        setPatients(uniquePatients);
        setFilteredPatients(uniquePatients);
      } catch (error) {
        console.error('Error fetching patients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, [getAllPatients]);

  // Filter patients based on input
  const filterPatients = (inputValue) => {
    if (inputValue === '' || !inputValue) {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(patient =>
        patient.firstName.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredPatients(filtered);
    }
    setSelectedIndex(-1);
  };

  // Handle patient selection
  const selectPatient = (patient, onChange) => {
    onChange(patient.firstName);
    setCurrentPatient({
      ...(fullPatientDetails || {}),
      firstName: patient.firstName,
    });
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, onChange, value) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredPatients.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredPatients.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredPatients.length) {
          selectPatient(filteredPatients[selectedIndex], onChange);
        } else {
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current && isOpen) {
      const dropdownContent = dropdownRef.current.querySelector('.dropdown-content');
      if (dropdownContent) {
        const selectedElement = dropdownContent.children[selectedIndex];
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest' });
        }
      }
    }
  }, [selectedIndex, isOpen]);

  // Update filtered patients when patients change
  useEffect(() => {
    setFilteredPatients(patients);
  }, [patients]);


  return (
    <div className="w-full relative" ref={dropdownRef}>
      <Controller
        control={control}
        name="firstName"
        render={({ field: { onChange, value, onBlur } }) => (
          <div className="relative w-full">
            <input
              value={value || patientName || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                onChange(newValue);
                filterPatients(newValue);
                setCurrentPatient({
                  ...fullPatientDetails,
                  firstName: newValue,
                });
                if (!isOpen) setIsOpen(true);
              }}
              onFocus={() => {
                setIsOpen(true);
                filterPatients(value || '');
              }}
              onBlur={(e) => {
                // Delay the onBlur to allow for dropdown clicks
                setTimeout(() => {
                  onBlur();
                  if (!dropdownRef.current?.contains(document.activeElement)) {
                    setIsOpen(false);
                    setSelectedIndex(-1);
                  }
                }, 150);
              }}
              onKeyDown={(e) => handleKeyDown(e, onChange, value)}
              className="p-2 w-full h-[38px] text-base input-field-classes border-gray-300"
              placeholder="Patient's name"
              autoComplete="off"
            />

            {/* Loading indicator */}
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-900"></div>
              </div>
            )}

            {/* Dropdown */}
            {isOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                <div className="dropdown-content">
                  {filteredPatients.length === 0 ? (
                    <div className="px-4 py-2 text-gray-500">
                      {loading ? 'Loading...' : 'No Patient Found'}
                    </div>
                  ) : (
                    filteredPatients.map((patient, index) => (
                      <div
                        key={patient.id}
                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${index === selectedIndex ? 'bg-blue-100' : ''
                          }`}
                        onClick={() => selectPatient(patient, onChange)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        <div className="font-medium text-base">{patient.firstName}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      />

      <div className="h-2">
        {errors && errors.firstName ? (
          <span className="text-red">{errors.firstName.message}</span>
        ) : null}
      </div>
    </div>
  );
};

export default SearchablePatientDropdown;